import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import Typewriter from "typewriter-effect";
import AOS from "aos";
import "aos/dist/aos.css";
import { FiFolder, FiTool, FiFileText } from "react-icons/fi";
import { FiCpu, FiCode, FiZap, FiUsers, FiCloud } from "react-icons/fi";
import { SiReact, SiPython, SiDjango, SiTensorflow, SiDocker, SiPostgresql, SiMariadb } from "react-icons/si";
import { FaJava } from "react-icons/fa";
import { MdDeveloperMode } from "react-icons/md";

import profileImg from "../assets/profile.jpg";
import ProjectsPreviewCarousel from "../components/ProjectsPreviewCarousel";
import GitHubActivity from "../components/GitHubActivity";

export default function Home() {
  const [totalProjects, setTotalProjects] = useState(0);
  const [uniqueTechnologies, setUniqueTechnologies] = useState(0);
  const [projects, setProjects] = useState([]);
  const researchPapers = 2;

  useEffect(() => { AOS.init({ duration: 800, once: true }); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/data/projects.json");
        const data = await res.json();
        setProjects(data);
        setTotalProjects(data.length);
        const techSet = new Set();
        data.forEach((p) => (p.technologies || []).forEach((t) => techSet.add(t)));
        setUniqueTechnologies(techSet.size);
      } catch (err) { console.error("❌ Failed to load projects:", err.message); }
    })();
  }, []);

  // Close dropdown when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (e) => {
      const dropdown = document.querySelector('.dropdown-menu.show-dropdown');
      if (dropdown && !e.target.closest('.dropdown-trigger')) {
        dropdown.classList.remove('show-dropdown');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);


  return (
    <>
      {/* ===== HERO ===== */}
      <section id="hero" className="relative min-h-[90vh] flex flex-col justify-center items-center text-center px-6 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-tr from-blue-50 to-purple-100 dark:from-slate-900 dark:to-slate-800 opacity-40 blur-2xl" />

        {/* Profile Image - Larger size */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative w-48 h-48 lg:w-56 lg:h-56 mb-4"
          data-aos="zoom-in"
        >
          <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-400 to-purple-600 p-1">
            <div className="w-full h-full rounded-full bg-white dark:bg-slate-800 p-1">
              <img src={profileImg} alt="Profile" className="w-full h-full rounded-full object-cover shadow-md" />
            </div>
          </div>
        </motion.div>

        {/* Compact Availability Status - Under Profile */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mb-6 relative"
        >
          <div className="flex items-center gap-3 px-4 py-2 bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-600 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[3px_3px_0px_0px_rgba(148,163,184,0.5)]">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-3 h-3 rounded-full bg-green-500"
            />
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-100">
              Open to opportunities
            </span>
            <span className="text-slate-600 dark:text-slate-400 text-sm">•</span>
            <span
              className="relative group text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors cursor-pointer dropdown-trigger"
              onClick={(e) => {
                e.stopPropagation();
                const dropdown = e.currentTarget.querySelector('.dropdown-menu');
                if (dropdown) {
                  dropdown.classList.toggle('show-dropdown');
                }
              }}
            >
              Get in touch →

              {/* Dropdown Menu - Appears directly below the trigger */}
              <div className="dropdown-menu absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 max-w-[90vw] opacity-0 invisible transition-all duration-200 z-[60] pointer-events-none">
                <div className="bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-600 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(148,163,184,0.5)] overflow-hidden">
                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/256753257102"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors border-b-2 border-slate-900 dark:border-slate-600 group/item pointer-events-auto"
                  >
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-slate-900 dark:text-slate-100 group-hover/item:text-green-700 dark:group-hover/item:text-green-400 transition-colors">WhatsApp</div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">Quick response</div>
                    </div>
                    <svg className="w-4 h-4 text-slate-400 dark:text-slate-500 group-hover/item:text-slate-600 dark:group-hover/item:text-slate-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>

                  {/* Gmail */}
                  <a
                    href="mailto:munjijames02@gmail.com"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group/item pointer-events-auto"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <path d="M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z" fill="#EA4335" />
                    </svg>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-slate-900 dark:text-slate-100 group-hover/item:text-blue-700 dark:group-hover/item:text-blue-400 transition-colors">Gmail</div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">Professional inquiries</div>
                    </div>
                    <svg className="w-4 h-4 text-slate-400 dark:text-slate-500 group-hover/item:text-slate-600 dark:group-hover/item:text-slate-300 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </span>
          </div>
        </motion.div>

        {/* Add custom CSS for dropdown */}
        <style jsx>{`
          .dropdown-menu.show-dropdown {
            opacity: 1 !important;
            visibility: visible !important;
            pointer-events: auto !important;
          }
        `}</style>

        {/* Social Proof Badges */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex flex-wrap justify-center gap-3 mb-6"
        >
          {/* First Class Honours Badge */}
          <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-amber-500 dark:from-yellow-500 dark:to-amber-600 border-2 border-slate-900 dark:border-slate-600 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[3px_3px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
          >
            <svg className="w-5 h-5 text-slate-900 dark:text-slate-900" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-900">First Class Honours</span>
          </motion.div>

          {/* Currently Employed Badge */}
          <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-400 to-emerald-500 dark:from-green-500 dark:to-emerald-600 border-2 border-slate-900 dark:border-slate-600 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[3px_3px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
          >
            <svg className="w-5 h-5 text-slate-900 dark:text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-900">Software Developer Intern @ UCU</span>
          </motion.div>

          {/* Projects Badge */}
          <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-400 to-cyan-500 dark:from-blue-500 dark:to-cyan-600 border-2 border-slate-900 dark:border-slate-600 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[3px_3px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
          >
            <svg className="w-5 h-5 text-slate-900 dark:text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-900">{totalProjects}+ Projects</span>
          </motion.div>

          {/* GitHub Contributions Badge */}
          <motion.div
            whileHover={{ scale: 1.05, y: -2 }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-400 to-pink-500 dark:from-purple-500 dark:to-pink-600 border-2 border-slate-900 dark:border-slate-600 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-[3px_3px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
          >
            <svg className="w-5 h-5 text-slate-900 dark:text-slate-900" fill="currentColor" viewBox="0 0 24 24">
              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
            </svg>
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-900">Active Contributor</span>
          </motion.div>
        </motion.div>

        <h1 data-aos="fade-up" className="text-4xl md:text-5xl font-bold text-blue-700 dark:text-blue-400 mb-4">
          Hi, I'm James —
          <span className="ml-2 inline-block text-purple-600 dark:text-purple-400">
            <Typewriter
              options={{
                strings: ["Cloud Engineer", "Full-Stack Developer", "AI Enthusiast", "Tech Problem Solver"],
                autoStart: true, loop: true,
              }}
            />
          </span>
        </h1>

        <p data-aos="fade-up" data-aos-delay="200" className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mb-3 leading-relaxed">
          <span className="font-semibold text-blue-800 dark:text-blue-400">First Class Honours Computer Science graduate</span> specializing in AI and full-stack development.
          Built <span className="font-semibold text-green-700 dark:text-green-400">disease detection systems serving 50+ farmers</span> with 94.5% accuracy,
          deployed <span className="font-semibold text-indigo-700 dark:text-indigo-400">flood prediction IoT in Uganda</span> protecting 3 communities,
          and engineered <span className="font-semibold text-purple-700 dark:text-purple-400">intelligent traffic management systems</span> reducing wait times by 40%.
        </p>

        {/* Tagline with integrated skills */}
        <div data-aos="fade-up" data-aos-delay="300" className="mb-8 max-w-3xl">
          <p className="italic text-blue-500 mb-4">
            Turning ideas into impactful solutions through code, design, and creativity 🚀
          </p>

          {/* Core Technologies inline */}
          <div className="flex flex-wrap justify-center gap-2 items-center">
            <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Specializing in:</span>
            {[
              { name: "React", icon: <SiReact />, color: "from-blue-400 to-cyan-400" },
              { name: "Python", icon: <SiPython />, color: "from-blue-500 to-yellow-400" },
              { name: "Java", icon: <FaJava />, color: "from-red-500 to-orange-500" },
              { name: "Django", icon: <SiDjango />, color: "from-green-600 to-emerald-500" },
              { name: "AI/ML", icon: <SiTensorflow />, color: "from-orange-500 to-amber-400" },
              { name: "Docker", icon: <SiDocker />, color: "from-blue-500 to-sky-400" },
              { name: "PostgreSQL", icon: <SiPostgresql />, color: "from-blue-600 to-indigo-500" },
              { name: "MariaDB", icon: <SiMariadb />, color: "from-blue-700 to-blue-900" },
              { name: "Azure", icon: <FiCloud />, color: "from-blue-600 to-blue-500" },
              { name: "IoT", icon: <MdDeveloperMode />, color: "from-purple-500 to-pink-500" },
            ].map((tech, i) => (
              <motion.div
                key={i}
                whileHover={{
                  scale: 1.15,
                  y: -4,
                  rotate: [0, -5, 5, 0],
                  transition: { duration: 0.3 }
                }}
                whileTap={{ scale: 0.95 }}
                className={`group relative px-3 py-1.5 rounded-full bg-gradient-to-r ${tech.color} text-white font-semibold text-sm shadow-md cursor-pointer overflow-hidden`}
              >
                {/* Glow effect on hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${tech.color} opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-300`} />

                {/* Shine effect */}
                <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />

                <span className="relative z-10 inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 inline-flex items-center justify-center transition-transform group-hover:rotate-12 group-hover:scale-110 duration-300">
                    {tech.icon}
                  </span>
                  {tech.name}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats - Enhanced */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-8 mb-12" data-aos="fade-up" data-aos-delay="400">
          {[
            { label: "Projects", value: totalProjects, icon: <FiFolder size={32} />, color: "from-blue-500 to-indigo-600", accentColor: "blue-600" },
            { label: "Technologies", value: uniqueTechnologies, icon: <FiTool size={32} />, color: "from-purple-500 to-pink-500", accentColor: "purple-600" },
            { label: "Research Papers", value: researchPapers, icon: <FiFileText size={32} />, color: "from-green-500 to-teal-600", accentColor: "green-600" },
          ].map(({ label, value, icon, color, accentColor }, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: i * 0.1, duration: 0.5, type: "spring", stiffness: 100 }}
              whileHover={{
                y: -10,
                scale: 1.05,
                transition: { duration: 0.2 }
              }}
              className="group relative w-full sm:w-64 p-6 bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-600
                         text-center cursor-pointer overflow-hidden
                         shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[8px_8px_0px_0px_rgba(59,130,246,0.5)]
                         transition-all duration-300"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

              {/* Top accent bar */}
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${color} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`} />

              {/* Icon container with animation */}
              <motion.div
                className="relative z-10 mb-4 inline-flex items-center justify-center"
                whileHover={{
                  rotate: [0, -10, 10, -10, 0],
                  scale: 1.2
                }}
                transition={{ duration: 0.5 }}
              >
                {/* Glow effect behind icon */}
                <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-20 blur-2xl scale-150 group-hover:opacity-40 transition-opacity`} />

                {/* Icon */}
                <div className={`relative text-${accentColor} group-hover:text-white transition-colors duration-300 drop-shadow-lg`}>
                  {icon}
                </div>
              </motion.div>

              {/* Number with counter animation */}
              <div className="relative z-10">
                <motion.div
                  className={`text-4xl md:text-5xl font-black text-slate-900 dark:text-slate-100 mb-2 group-hover:text-${accentColor} transition-colors duration-300`}
                  whileHover={{ scale: 1.1 }}
                >
                  <CountUp
                    end={value}
                    duration={2.5}
                    separator=","
                    enableScrollSpy
                    scrollSpyOnce
                  />
                  {label !== "Research Papers" && (
                    <motion.span
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="text-blue-600 dark:text-blue-400"
                    >
                      +
                    </motion.span>
                  )}
                </motion.div>

                {/* Label */}
                <div className="text-sm font-bold uppercase tracking-wider text-slate-600 group-hover:text-slate-900 transition-colors">
                  {label}
                </div>
              </div>

              {/* Decorative corner */}
              <div className={`absolute bottom-0 right-0 w-0 h-0 border-b-[40px] border-b-${accentColor} border-l-[40px] border-l-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300`} />

              {/* Shine effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            </motion.div>
          ))}
        </div>

        {/* What I Do - Expanded Skills Showcase */}
        <div className="w-full max-w-6xl px-4 mt-16" data-aos="fade-up" data-aos-delay="500">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100 mb-3 relative inline-block">
              What I Do
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mt-4 text-sm font-mono">
              Core competencies & technical expertise
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Full-Stack Development",
                icon: <FiCode size={40} />,
                iconColor: "text-blue-600",
                bgGradient: "from-blue-500 to-indigo-600",
                desc: "Building modern web apps with React, Next.js, Django REST Framework, and FastAPI.",
                tags: ["React", "Django", "TailwindCSS", "REST APIs"]
              },
              {
                title: "AI & Machine Learning",
                icon: <FiCpu size={40} />,
                iconColor: "text-purple-600",
                bgGradient: "from-purple-500 to-pink-600",
                desc: "Developing intelligent systems with TensorFlow, YOLOv5, OpenCV, and scikit-learn.",
                tags: ["TensorFlow", "YOLO", "OpenCV", "Keras"]
              },
              {
                title: "Cloud & DevOps",
                icon: <FiZap size={40} />,
                iconColor: "text-cyan-500",
                bgGradient: "from-cyan-500 to-blue-600",
                desc: "Deploying scalable solutions with Docker, Azure, PostgreSQL, and CI/CD pipelines.",
                tags: ["Docker", "Azure", "PostgreSQL", "MongoDB"]
              },
              {
                title: "IoT & Embedded",
                icon: <FiUsers size={40} />,
                iconColor: "text-green-600",
                bgGradient: "from-green-500 to-emerald-600",
                desc: "Creating connected devices with Arduino, ESP32, sensors, and real-time systems.",
                tags: ["Arduino", "ESP32", "IoT", "TinkerCAD"]
              },
            ].map(({ title, icon, iconColor, bgGradient, desc, tags }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{
                  y: -8,
                  transition: { duration: 0.2 }
                }}
                className="group relative bg-white dark:bg-slate-800 border-2 border-slate-900 dark:border-slate-600 p-6 cursor-pointer overflow-hidden
                           shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(148,163,184,0.5)] hover:shadow-[6px_6px_0px_0px_rgba(59,130,246,0.5)]
                           transition-all duration-300"
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${bgGradient} opacity-0 group-hover:opacity-5 dark:group-hover:opacity-10 transition-opacity duration-300`} />

                {/* Corner accent */}
                <div className={`absolute top-0 right-0 w-0 h-0 border-t-[30px] border-t-transparent group-hover:border-t-blue-600 dark:group-hover:border-t-blue-400 border-l-[30px] border-l-transparent opacity-0 group-hover:opacity-100 transition-all duration-300`} />

                {/* Icon with animated background */}
                <motion.div
                  className="relative mb-4 inline-flex items-center justify-center"
                  whileHover={{ rotate: [0, -5, 5, 0], scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${bgGradient} opacity-10 rounded-full blur-xl scale-150 group-hover:opacity-20 transition-opacity`} />
                  <div className={`relative ${iconColor} dark:brightness-110 transition-transform group-hover:scale-110 duration-300`}>
                    {icon}
                  </div>
                </motion.div>

                {/* Title */}
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                  {title}
                </h3>

                {/* Description */}
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 leading-relaxed group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors">
                  {desc}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-3 border-t-2 border-slate-200 dark:border-slate-700 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                  {tags.map((tag, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 + idx * 0.05 }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      className="text-[10px] font-bold px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 uppercase tracking-wide
                                 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300 transition-all cursor-pointer
                                 border border-transparent hover:border-blue-300 dark:hover:border-blue-500"
                    >
                      {tag}
                    </motion.span>
                  ))}
                </div>

                {/* Number badge */}
                <motion.div
                  className={`absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br ${bgGradient} 
                             flex items-center justify-center font-black text-white text-lg
                             border-2 border-slate-900 dark:border-slate-600 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-[2px_2px_0px_0px_rgba(148,163,184,0.5)]
                             group-hover:scale-110 transition-transform duration-300`}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: i * 0.1 + 0.2, type: "spring", stiffness: 200 }}
                >
                  {i + 1}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ===== GITHUB ACTIVITY ===== */}
      <GitHubActivity />


      {/* ===== TIMELINE CTA ===== */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-slate-900 dark:to-slate-800 py-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-6">
              📅 My Journey Through Time
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              From secondary school to First Class graduate to professional software engineer —
              explore the complete timeline of my educational and professional milestones.
            </p>
            <motion.a
              href="/timeline"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-500 dark:to-purple-500 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              View Full Timeline →
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ===== PROJECTS PREVIEW ===== */}
      <section className="w-full py-16 bg-white dark:bg-slate-900 transition-colors duration-300" data-aos="fade-up" data-aos-delay="200">
        <ProjectsPreviewCarousel data={projects} />
      </section>
    </>
  );
}

